import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const EmailComposerModal = ({ isOpen, onClose, event, attendees, user, agendaItems }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Generate default subject and message when modal opens
  React.useEffect(() => {
    if (isOpen && event) {
      const defaultSubject = `EVENT INFO: ${event.title}`;
      const eventDate = new Date(event.start_datetime).toLocaleDateString();
      const eventTime = event.all_day ? 'All Day' : 
        `${new Date(event.start_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - ${new Date(event.end_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`;
      
      const goingAttendees = attendees.filter(rsvp => rsvp.rsvp_status === 'going');
      const totalGuestCount = goingAttendees.reduce((sum, rsvp) => sum + (rsvp.guest_count || 0), 0);
      const totalAttendees = goingAttendees.length + totalGuestCount;

      let attendeeList = goingAttendees.map(rsvp => {
        const name = rsvp.user_name || `${rsvp.first_name || ''} ${rsvp.last_name || ''}`.trim() || 'Attendee';
        const guestCount = rsvp.guest_count || 0;
        return guestCount > 0 ? `${name} (+${guestCount} guest${guestCount > 1 ? 's' : ''})` : name;
      }).join('\n• ');

      let paymentInfo = '';
      if (event.is_paid && event.price) {
        paymentInfo = `\n\nPAYMENT INFORMATION:
This is a paid event: $${event.price} per person
Please ensure your payment is processed before the event.
Check the Payments section in your account for details.`;
      }

      // Add agenda section if available
      let agendaSection = '';
      if (agendaItems && agendaItems.length > 0) {
        const agendaList = agendaItems.map(item => {
          let itemText = `${item.order}. ${item.title}`;
          if (item.notes) {
            itemText += `\n   Notes: ${item.notes}`;
          }
          return itemText;
        }).join('\n');
        
        agendaSection = `\n\nMEETING AGENDA:
${agendaList}`;
      }

      const defaultMessage = `Dear Event Attendees,

Below is your information for the upcoming event:

EVENT: ${event.title}
DATE: ${eventDate}
TIME: ${eventTime}
${event.location ? `LOCATION: ${event.location}` : ''}

${event.description ? `DETAILS: ${event.description}` : ''}${agendaSection}

CONFIRMED ATTENDEES (${totalAttendees} total):
• ${attendeeList}${paymentInfo}

We look forward to seeing you there!

Best regards,
${event.organizer || user?.first_name + ' ' + user?.last_name || 'Event Organizer'}

---
This email was sent through the HOA system. Please reply only to the organizer.`;

      setSubject(defaultSubject);
      setMessage(defaultMessage);
    }
  }, [isOpen, event, attendees, user, agendaItems]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      alert('Please fill in both subject and message');
      return;
    }

    const goingAttendees = attendees.filter(rsvp => rsvp.rsvp_status === 'going');
    const emails = goingAttendees
      .filter(rsvp => rsvp.email)
      .map(rsvp => rsvp.email);

    if (emails.length === 0) {
      alert('No email addresses found for going attendees');
      return;
    }

    setSending(true);
    try {
      // Call backend API to send emails
      const token = localStorage.getItem('hoa_token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/events/send-bulk-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_id: event.id || event._id,
          subject: subject,
          message: message,
          recipient_emails: emails,
          sender_name: user?.first_name + ' ' + user?.last_name || 'Event Organizer'
        })
      });

      if (response.ok) {
        alert('Emails sent successfully!');
        onClose();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send emails');
      }
    } catch (error) {
      console.error('Error sending emails:', error);
      alert('Error sending emails: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const goingAttendees = attendees ? attendees.filter(rsvp => rsvp.rsvp_status === 'going') : [];
  const emails = goingAttendees.filter(rsvp => rsvp.email).map(rsvp => rsvp.email);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">📧 Email Event Attendees</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Recipient Info */}
          <div className="bg-blue-50 p-3 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Recipients ({emails.length} attendees)</h4>
            <p className="text-sm text-blue-700">
              This email will be sent to all attendees who RSVP'd "going" and have email addresses on file.
              All emails will be sent via BCC to protect privacy.
            </p>
            {emails.length > 0 && (
              <div className="mt-2 text-xs text-blue-600">
                Recipients: {emails.slice(0, 3).join(', ')}{emails.length > 3 && ` and ${emails.length - 3} more...`}
              </div>
            )}
          </div>

          {/* Subject Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email subject..."
            />
          </div>

          {/* Message Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
            <textarea
              rows="12"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your message..."
            />
          </div>

          {/* Event Info Preview */}
          {event && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-1">Event: {event.title}</h4>
              <p className="text-sm text-gray-600">
                {new Date(event.start_datetime).toLocaleDateString()} • {goingAttendees.length} attending
                {event.is_paid && ` • $${event.price} per person`}
              </p>
              
              {/* Agenda Preview */}
              {agendaItems && agendaItems.length > 0 && (
                <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <h5 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                    <span>📋</span> Meeting Agenda (will be included in email)
                  </h5>
                  <ol className="space-y-1 text-sm text-gray-700">
                    {agendaItems.slice(0, 5).map((item, index) => (
                      <li key={item.id || index} className="flex gap-2">
                        <span className="font-medium text-purple-700">{item.order}.</span>
                        <div className="flex-1">
                          <span>{item.title}</span>
                          {item.notes && (
                            <p className="text-xs text-gray-600 mt-1">{item.notes}</p>
                          )}
                        </div>
                      </li>
                    ))}
                    {agendaItems.length > 5 && (
                      <li className="text-xs text-purple-600 italic">
                        ... and {agendaItems.length - 5} more agenda items
                      </li>
                    )}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !message.trim() || emails.length === 0}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {sending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                Sending...
              </>
            ) : (
              `Send to ${emails.length} attendees`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposerModal;